import React from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Text } from '@components'

export type NotificationTabKey = 'all' | 'requests' | 'agenda'

export interface NotificationTab {
  key: NotificationTabKey
  label: string
  count: number
}

interface NotificationTabsProps {
  tabs: NotificationTab[]
  activeKey: NotificationTabKey
  onChange: (key: NotificationTabKey) => void
}

/**
 * Segmented pill control for the notifications filter. The active segment is a
 * raised white card on a tinted track, giving the screen a clear, modern focus
 * state instead of the old underline tabs.
 */
export const NotificationTabs = ({
  tabs,
  activeKey,
  onChange,
}: NotificationTabsProps): React.ReactElement => {
  const theme = useTheme()
  const styles = getStyle(theme)

  return (
    <View style={styles.track}>
      {tabs.map((tab) => {
        const isActive = tab.key === activeKey
        return (
          <TouchableOpacity
            key={tab.key}
            activeOpacity={0.8}
            onPress={() => onChange(tab.key)}
            style={[styles.segment, isActive && styles.segmentActive]}
          >
            <Text
              type={isActive ? 'bodySmallBold' : 'bodySmall'}
              color={isActive ? theme.colors.primary : theme.colors.greyBlue}
            >
              {tab.label}
            </Text>
            {tab.count > 0 && (
              <View style={[styles.badge, !isActive && styles.badgeMuted]}>
                <Text type="microText" color={theme.colors.white} style={styles.badgeText}>
                  {tab.count > 99 ? '99+' : String(tab.count)}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        )
      })}
    </View>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    track: {
      flexDirection: 'row',
      backgroundColor: theme.colors.grey10,
      borderRadius: theme.border.large,
      padding: theme.spacing.xxs,
      marginBottom: theme.spacing.lg,
    },
    segment: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.xs,
      paddingVertical: theme.spacing.sm + theme.spacing.xxs,
      borderRadius: theme.border.medium,
    },
    segmentActive: {
      backgroundColor: theme.colors.white,
      shadowColor: theme.colors.black,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.08,
      shadowRadius: 6,
      elevation: 3,
    },
    badge: {
      minWidth: 18,
      height: 18,
      paddingHorizontal: theme.spacing.xs,
      borderRadius: theme.border.small,
      backgroundColor: theme.colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    badgeMuted: {
      backgroundColor: theme.colors.greyBlue,
    },
    badgeText: {
      fontWeight: 'bold',
    },
  })
